/**
 * FICHIER CLIENTS — la LISTE de la maquette `isClients`, portée sur le TABLEAU DU KIT.
 *
 * ⚠️ MÊME COMPORTEMENT QUE LE CATALOGUE (décision utilisateur 2026-08-30) : `DataTable` du kit —
 *    en-tête COLLANT, défilement INTERNE au cadre (plus de page interminable), pagination et compteur
 *    AU PIED (et non à côté du bouton Colonnes), colonnes masquables/déplaçables mémorisées sur le
 *    profil. Une seule mécanique de tableau dans tout le back-office : deux se seraient mises à
 *    diverger (tri, vide, colonnes) sur l'écran qu'on relit le moins.
 *
 * Ce que la page SERT : les 4 repères (fiches, cagnotte en circulation, joignables email,
 * anniversaires du mois), la recherche, les puces comptées SANS la puce active, les VISITES et le CA
 * dérivés des pièces fiscales, l'ouverture de la fiche, et l'export CSV du jeu FILTRÉ ENTIER.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Badge, DataTable, KpiRow, showToast, Tooltip, Trans, useLatest, type ColumnDef, type KpiItem, type SortState, type TableStatus } from '@orgazone/ui-web';
import type { ColumnLayout } from '@orgazone/ui-tokens';
import { currentCurrency, formatMoney } from '@orgazone/money-format';
import { api, type ClientChip, type ClientNumberingReport, type ClientRow, type ClientSortKey, type ClientsSummary, type TablePrefs } from '../api';
import { useStoreScope } from '../store-context';
import { exporterCsv } from './export-csv';
import { bdL, merchantBalanceView, palierPastille } from './clients-kit';
import { FicheClient } from './clients-fiche';
import { key, t, type Translatable } from '../i18n';

const TABLE_ID = 'clients';
const PAGE_SIZE = 25;
/** Le plafond serveur d'un lot — borne aussi les pages de l'export. */
const LOT_MAX = 1000;
const MONO = 'var(--font-mono)';

const DEFAULT_SORT: SortState = { key: 'name', dir: 'asc' };
const DEFAULT_LAYOUT: ColumnLayout = { order: ['name', 'card', 'tel', 'email', 'b', 'g', 'v', 'ca'], hidden: [] };
const TRIABLES: ClientSortKey[] = ['name', 'card', 'tel', 'email', 'b', 'g', 'v', 'ca'];
const estTriClient = (k: string): k is ClientSortKey => (TRIABLES as string[]).includes(k);

/** Les puces de la maquette (`clChips`) — des filtres FERMÉS, jamais du texte libre. */
const CHIPS: { v: ClientChip; l: Translatable }[] = [
  { v: 'gain', l: key('Avec cagnotte') }, { v: 'wallet', l: key('Wallet') }, { v: 'bmois', l: key('Anniv. ce mois') },
  { v: 'noemail', l: key('Sans email') }, { v: 'notel', l: key('Sans téléphone') },
  // ⚠️ EN DERNIER MAIS PAS ACCESSOIRE : une demande d'effacement a un délai légal, et elle arrive par
  //    la caisse — sans cette puce, elle n'existerait que sur la fiche du client concerné, donc pour
  //    qui sait déjà qui il est.
  { v: 'rgpd', l: key('Demande RGPD') },
];

const nomDe = (c: ClientRow): string => (c.erased ? t('Client anonymisé') : `${c.lastName} ${c.firstName ?? ''}`.trim());
const initialesDe = (c: ClientRow): string => (c.erased ? '×' : `${c.lastName[0] ?? ''}${c.firstName?.[0] ?? ''}`.toUpperCase());
const ageDe = (b: string | null): string => (b ? t('{n} an', { n: Math.floor((Date.now() - new Date(b).getTime()) / 31_557_600_000) }) : '');
const annivCeMois = (b: string | null): boolean => !!b && Number(b.slice(5, 7)) === new Date().getMonth() + 1;
const euros = (cents: number): string => formatMoney(cents, { thousandsSep: ' ' });

/** L'icône gâteau de la maquette — anniversaire ce mois-ci. */
function Gateau() {
  return (
    <span title={t('Anniversaire ce mois-ci')} style={{ color: 'var(--teal-600)', display: 'flex' }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M3 8h18v4H3zM5 12h14v9H5zM12 8v13M8 8a2.5 2.5 0 1 1 0-5c2 0 4 5 4 5s2-5 4-5a2.5 2.5 0 1 1 0 5" />
      </svg>
    </span>
  );
}

/**
 * LES FICHES SANS NUMÉRO CLIENT — le bandeau qui rend un défaut silencieux visible, et l'outil pour le
 * réparer au même endroit.
 *
 * ⚠️ POURQUOI CE BANDEAU EXISTE (audit du producteur, 2026-09-01). Le numéro client est l'identité par
 * laquelle le producteur de données maîtres adresse une fiche. Sans lui, le palier de fidélité n'est
 * JAMAIS calculé : la caisse applique le taux de base à vie, et **rien nulle part ne le disait**. Sur la
 * base bêta, 11 450 fiches reprises sont dans ce cas — la reprise legacy avait sauté l'attribution
 * faute de code « S » du magasin, et l'avait dit à un rapport que plus personne ne relit.
 * ⚠️ MUET QUAND IL N'Y A RIEN (`missing === 0`) : un bandeau permanent cesse d'être lu.
 * ⚠️ L'ÉTAT SE LIT SANS ÉCRIRE : le GET est une simulation, seul le bouton déclenche l'écriture.
 */
function NumberingBanner({ gln }: { gln: string | null }) {
  const [report, setReport] = useState<ClientNumberingReport | null>(null);
  const [running, setRunning] = useState(false);

  // ⚠️ SEULE LA DERNIÈRE LECTURE ÉCRIT LE BANDEAU : le compte de l'ancien magasin, revenu en dernier, annoncerait
  //    des fiches sans numéro que ce magasin n'a pas — et « Numéroter » s'appliquerait au magasin affiché.
  const latest = useLatest();
  const reload = useCallback(() => {
    if (!gln) return;
    const isLatest = latest();
    // ⚠️ Best-effort ASSUMÉ : l'écran Clients doit s'ouvrir même si cette lecture échoue — elle
    //    signale un défaut annexe, elle n'est pas la raison d'être de la page.
    api.clientNumbering()
      .then((r) => { if (isLatest()) setReport(r); })
      .catch(() => { if (isLatest()) setReport(null); });
  }, [gln, latest]);
  useEffect(reload, [reload]);

  if (!report || report.missing === 0) return null;

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.runClientNumbering();
      setReport(r);
      // ⚠️ LE MESSAGE DIT CE QUI S'EST VRAIMENT PASSÉ, y compris un refus : « 0 numéroté » sans motif
      //    se lirait comme « il n'y avait rien à faire » (§23.0).
      if (r.blocked) showToast(r.blocked, 'err');
      else if (r.remaining > 0) showToast(t('{n} fiche numérotée — il en reste {remaining}, relancez.', { n: r.numbered, remaining: r.remaining }), 'ok');
      else showToast(t('{n} fiche numérotée. Plus aucune fiche sans numéro.', { n: r.numbered }), 'ok');
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally { setRunning(false); }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', marginBottom: 10,
      border: '1px solid var(--amber-fg)', borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)',
    }}>
      <span style={{ flex: 1, minWidth: 0, fontSize: '12.5px', color: 'var(--amber-fg)' }}>
        <strong>{t('{n} fiche sans n° client.', { n: report.missing })}</strong>{' '}
        
        {t('Leur palier de fidélité n’est pas calculé : la caisse leur applique le taux de base.')}
        {report.blocked ? ` ${report.blocked}` : ''}
      </span>
      {/* ⚠️ PAS DE BOUTON QUAND IL NE PEUT RIEN FAIRE : un bouton actif qui répondrait « impossible »
          serait l'interdit n°4 du §23.0. Le motif est déjà dans le texte ci-dessus. */}
      {!report.blocked && (
        <button type="button" onClick={run} disabled={running}
          style={{
            height: 30, padding: '0 13px', border: 'none', borderRadius: 'var(--r-ctrl)',
            background: 'var(--amber-fg)', color: 'var(--paper)', fontSize: '12.5px', fontWeight: 700,
            cursor: running ? 'default' : 'pointer', opacity: running ? 0.6 : 1, whiteSpace: 'nowrap',
          }}>
          {running ? t('Numérotation…') : t('Numéroter maintenant')}
        </button>
      )}
    </div>
  );
}

export function Clients() {
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;

  const [q, setQ] = useState('');
  const [applied, setApplied] = useState('');
  const [chip, setChip] = useState<ClientChip | ''>('');
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [layout, setLayout] = useState<ColumnLayout>(DEFAULT_LAYOUT);
  const [reglagesLus, setReglagesLus] = useState(false);
  const [memoKo, setMemoKo] = useState<string | null>(null);

  const [rows, setRows] = useState<ClientRow[]>([]);
  const [total, setTotal] = useState<number | 'unknown'>('unknown');
  const [resume, setResume] = useState<ClientsSummary | null>(null);
  const [status, setStatus] = useState<TableStatus>({ phase: 'idle' });
  const [essai, setEssai] = useState(0);
  const [exportEnCours, setExportEnCours] = useState(false);
  /** La fiche ouverte : `{ id: null }` = création, `{ id }` = fiche existante, `null` = fermée. */
  const [record, setFiche] = useState<{ id: string | null } | null>(null);

  const location = useLocation();
  const navigate = useNavigate();

  // ⚠️ LA PALETTE ⌘K ARRIVE ICI : un client choisi OUVRE SA FICHE directement. L'état de navigation
  //    se consomme UNE fois (replace) — un retour arrière ne rouvre rien.
  useEffect(() => {
    const graine = (location.state as { ficheDepuisPalette?: string } | null)?.ficheDepuisPalette;
    if (!graine) return;
    setFiche({ id: graine });
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- l'état de navigation ne se consomme qu'une fois
  }, [location.state]);

  // La recherche part après un temps de calme, et REVIENT EN PAGE 1 (la page 7 d'un autre jeu n'existe pas).
  useEffect(() => {
    const timer = setTimeout(() => { setApplied(q.trim()); setPageIndex(0); }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  // Réglages d'écran de la PERSONNE — best-effort assumé : sans eux, l'écran ouvre sur ses défauts.
  useEffect(() => {
    if (!gln) return;
    let vivant = true;
    setReglagesLus(false);
    api.tablePrefs(TABLE_ID)
      .then((p) => {
        if (!vivant) return;
        if (p.layout) setLayout({ order: p.layout.order, hidden: p.layout.hidden });
        if (p.pageSize) setPageSize(p.pageSize);
        // ⚠️ Un filtre restauré DOIT SE VOIR : il repart dans l'état, donc dans les puces révocables.
        const puce = p.filters?.chip;
        if (puce && CHIPS.some((c) => c.v === puce)) setChip(puce as ClientChip);
      })
      .catch(() => undefined)
      .finally(() => { if (vivant) setReglagesLus(true); });
    return () => { vivant = false; };
  }, [gln]);

  // ⚠️ JAMAIS AVANT D'AVOIR RELU : enregistrer au premier rendu écraserait le réglage mémorisé.
  // ⚠️ SEUL LE DERNIER ENREGISTREMENT DIT SON ISSUE : deux enregistrements qui se croisent afficheraient « réglage non
  //    mémorisé » alors que le dernier a réussi — ou effaceraient l'échec du dernier par la réussite d'un ancien.
  const latestPrefs = useLatest();
  useEffect(() => {
    if (!gln || !reglagesLus) return;
    const timer = setTimeout(() => {
      const prefs: TablePrefs = {
        layout: { order: [...layout.order], hidden: [...layout.hidden] },
        pageSize,
        ...(chip ? { filters: { chip } } : { filters: {} }),
      };
      const isLatest = latestPrefs();
      api.saveTablePrefs(TABLE_ID, prefs)
        .then(() => { if (isLatest()) setMemoKo(null); })
        .catch((e: unknown) => { if (isLatest()) setMemoKo(e instanceof Error ? e.message : String(e)); });
    }, 600);
    return () => clearTimeout(timer);
  }, [gln, reglagesLus, layout, pageSize, chip, latestPrefs]);

  // ⚠️ SEULE LA DERNIÈRE LECTURE ÉCRIT LA PAGE : une recherche, un tri, une page ou un magasin changés pendant une
  //    lecture la relancent — la réponse de l'ancienne, revenue en dernier, afficherait des clients que la recherche ne
  //    demande plus, sous un total qui ne les décrit pas.
  const latestList = useLatest();
  const load = useCallback(async () => {
    if (!gln) return;
    const isLatest = latestList();
    setStatus((s) => (s.phase === 'ready' ? { phase: 'reloading' } : { phase: 'loading' }));
    try {
      const p = await api.clients({
        q: applied, chip, sort: estTriClient(sort.key) ? sort.key : 'name', dir: sort.dir,
        limit: pageSize, offset: pageIndex * pageSize,
      });
      if (!isLatest()) return;
      setRows(p.rows);
      setTotal(p.total);
      // ⚠️ LE RÉSUMÉ NE VIENT QU'AU PREMIER LOT (serveur) : il porte sur la vue de RECHERCHE, donc il
      //    est le même sur toutes les pages d'un même filtre — on garde le dernier connu en paginant,
      //    et une nouvelle recherche repart en page 1, donc le rafraîchit.
      if (p.summary) setResume(p.summary);
      setStatus({ phase: 'ready' });
    } catch (e) {
      if (!isLatest()) return;
      setStatus({
        phase: 'error',
        message: t('Le fichier clients n’a pas pu être lu.'),
        detail: e instanceof Error ? e.message : String(e),
        onRetry: () => setEssai((n) => n + 1),
      });
    }
  }, [gln, applied, chip, sort, pageIndex, pageSize, latestList]);
  // ⚠️ `essai` déclenche la RELANCE (bouton « Réessayer » de l'état d'erreur) : il vit ICI et non dans
  //    les dépendances de `charger`, qui ne le lit pas — une dépendance non lue est un signal faux.
  useEffect(() => { void load(); }, [load, essai]);

  const colonnes = useMemo<ColumnDef<ClientRow>[]>(() => [
    {
      key: 'name',
      label: t('Client'),
      // Identité de la ligne : ni masquable, ni déplaçable — la lire ailleurs qu'en tête rendrait le
      // tableau illisible sans que rien ne l'annonce.
      locked: true,
      pinned: 'start',
      sortable: true,
      defaultSortDir: 'asc',
      minWidth: 240,
      render: (c) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span style={{ width: 32, height: 32, borderRadius: 'var(--r-pill)', background: c.erased ? 'var(--ink-100)' : 'var(--teal-100)', color: c.erased ? 'var(--ink-400)' : 'var(--teal-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11.5px', fontWeight: 700, flexShrink: 0 }}>{initialesDe(c)}</span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span style={{ fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nomDe(c)}</span>
              {c.tierDisplay && !c.erased && (
                <Badge tone="neutral" size="sm" radius="pill" style={{ background: palierPastille(c.tierDisplay).bg, color: palierPastille(c.tierDisplay).fg }}>
                  {palierPastille(c.tierDisplay).l}
                </Badge>
              )}
              {c.wallet && !c.erased && <Badge tone="warn" size="sm" radius="pill">{t('Wallet')}</Badge>}
            </span>
            <span style={{ display: 'block', fontFamily: MONO, fontSize: '10px', color: 'var(--ink-400)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.clientNumber ?? '—'}</span>
          </span>
        </div>
      ),
    },
    {
      key: 'card', label: t('Carte'), sortable: true, width: 150,
      render: (c) => <span style={{ fontFamily: MONO, fontSize: '11.5px', color: c.loyaltyNumber ? 'var(--ink-600)' : 'var(--ink-300)' }}>{c.loyaltyNumber ?? '—'}</span>,
    },
    {
      key: 'tel', label: t('Téléphone'), sortable: true, width: 124, numeric: true,
      render: (c) => <span style={{ color: c.phone ? 'var(--ink-600)' : 'var(--ink-300)' }}>{c.phone ?? '—'}</span>,
    },
    {
      key: 'email', label: t('Email'), sortable: true, minWidth: 180,
      render: (c) => <span style={{ color: c.email ? 'var(--ink-600)' : 'var(--ink-300)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.email ?? '—'}</span>,
    },
    {
      key: 'b', label: t('Naissance'), sortable: true, width: 140,
      render: (c) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
          <span style={{ color: 'var(--ink-700)', fontVariantNumeric: 'tabular-nums' }}>{bdL(c.birthdate)}</span>
          {annivCeMois(c.birthdate) && <Gateau />}
          <span style={{ fontSize: '10.5px', color: 'var(--ink-400)' }}>{ageDe(c.birthdate)}</span>
        </span>
      ),
    },
    {
      key: 'g', label: t('Cagnotte'), sortable: true, defaultSortDir: 'desc', width: 108, numeric: true, align: 'end',
      // ⚠️ UN SOLDE NÉGATIF NE SE CACHE PAS (audit F22) : la condition était `> 0`, donc une cagnotte
      //    négative tombait dans le tiret — le commerçant lisait « rien » là où le client lui doit des
      //    points. Elle se lit maintenant en rouge, avec son motif au survol.
      render: (c) => {
        if (c.loyaltyCents === 0) return <span style={{ color: 'var(--ink-300)' }}>—</span>;
        const { tone, reason } = merchantBalanceView(c.loyaltyCents, c.negativeCause);
        const badge = <Badge tone={tone} size="sm" radius="pill">{euros(c.loyaltyCents)}</Badge>;
        return reason ? <Tooltip label={t('Cagnotte négative')} content={reason}>{badge}</Tooltip> : badge;
      },
    },
    {
      key: 'v', label: t('Visites'), sortable: true, defaultSortDir: 'desc', width: 84, numeric: true, align: 'end',
      render: (c) => <span style={{ fontWeight: 600, color: 'var(--ink-700)' }}>{c.visits}</span>,
    },
    {
      key: 'ca', label: 'CA', sortable: true, defaultSortDir: 'desc', width: 104, numeric: true, align: 'end',
      render: (c) => <span style={{ fontWeight: 600, color: 'var(--ink-900)' }}>{formatMoney(c.caCents, { thousandsSep: ' ', displayScale: 0 })}</span>,
    },
  ], []);

  // ⚠️ CHAQUE FILTRE APPLIQUÉ PORTE SA PUCE : un filtre qu'on ne voit pas fait lire « 12 clients »
  //    comme un fichier de 12 clients.
  const puces = [
    ...(applied ? [{ id: 'q', label: t('Recherche : « {applied} »', { applied }), onRemove: () => setQ('') }] : []),
    ...(chip ? [{ id: 'chip', label: (() => { const d = CHIPS.find((c) => c.v === chip); return d ? t(d.l) : chip; })(), onRemove: () => { setChip(''); setPageIndex(0); } }] : []),
  ];

  /**
   * EXPORT — le jeu FILTRÉ ENTIER, pas la page à l'écran (un export d'une page serait un piège).
   * ⚠️ Il RELIT le serveur par lots ; le toast dit combien de lignes sont parties.
   */
  const exporter = async () => {
    if (exportEnCours) return;
    setExportEnCours(true);
    try {
      const tout: ClientRow[] = [];
      for (let offset = 0; ; offset += LOT_MAX) {
        const p = await api.clients({ q: applied, chip, sort: estTriClient(sort.key) ? sort.key : 'name', dir: sort.dir, limit: LOT_MAX, offset });
        tout.push(...p.rows);
        if (p.rows.length < LOT_MAX || tout.length >= p.total) break;
      }
      const headers = [t('Nom'), t('Prénom'), t('N° client'), t('Carte'), t('Téléphone'), t('Email'), t('Naissance'), t('Cagnotte ({code})', { code: currentCurrency().code }), t('Visites'), t('CA ({code})', { code: currentCurrency().code })];
      exporterCsv('clients', headers, tout.map((c) => [
        c.erased ? t('Client anonymisé') : c.lastName, c.erased ? '' : (c.firstName ?? ''), c.clientNumber ?? '', c.loyaltyNumber ?? '',
        c.phone ?? '', c.email ?? '', bdL(c.birthdate) === '—' ? '' : bdL(c.birthdate),
        (c.loyaltyCents / 100).toFixed(2).replace('.', ','), c.visits, (c.caCents / 100).toFixed(2).replace('.', ','),
      ]));
      showToast(t('{n} ligne exportée — le jeu filtré entier.', { n: tout.length }));
    } catch (e) {
      showToast(t('Export interrompu : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err');
    } finally {
      setExportEnCours(false);
    }
  };

  /** Les 4 repères de la maquette (`clKpis`) — icônes et sous-titres verbatim. */
  const kpis: KpiItem[] = resume == null ? [] : [
    { label: t('Clients au fichier'), value: resume.records.toLocaleString('fr-FR'), sub: t('fiches actives de ce magasin'),
      icon: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0', tone: 'teal', },
    { label: t('Cagnotte en circulation'), value: euros(resume.cagnotteCents), sub: t('solde fidélité cumulé — engagement à honorer'),
      icon: 'M12 3l2.6 5.5 6 .9-4.3 4.2 1 6L12 16.8 6.7 19.6l1-6L3.4 9.4l6-.9z', tone: 'blue', },
    { label: t('Joignables par email'), value: resume.joignablesEmail.toLocaleString('fr-FR'), sub: t('adresse présente + opt-in — cible campagne'),
      icon: 'M4.5 5h15a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM3 7l9 7 9-7', tone: 'violet', },
    { label: t('Anniversaires ce mois'), value: resume.annivMois.toLocaleString('fr-FR'), sub: t('occasion de campagne — attention aux opt-out'),
      icon: 'M3 8h18v4H3zM5 12h14v9H5zM12 8v13M8 8a2.5 2.5 0 1 1 0-5c2 0 4 5 4 5s2-5 4-5a2.5 2.5 0 1 1 0 5', tone: 'amber', },
  ];

  return (
    <>
      {/* ── L'en-tête — titre + sous-titre du dessin + Export + Nouveau client ──────────────────── */}
      <header style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Fichier clients')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
            <Trans k='Une fiche par personne — <1>points, titres et historique tiennent à elle.</1> Cloisonné par magasin, sauf politique réseau contraire.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></p>
        </div>
        <button type="button" onClick={() => void exporter()} aria-disabled={exportEnCours || undefined}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 40, padding: '0 18px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '13.5px', fontWeight: 600, cursor: exportEnCours ? 'progress' : 'pointer' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" /></svg>
          {exportEnCours ? t('Export…') : t('Export CSV')}
        </button>
        <button type="button" onClick={() => setFiche({ id: null })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 40, padding: '0 18px', border: 'none', borderRadius: 'var(--r-sm)', background: 'var(--teal-500)', color: '#fff', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 5v14M5 12h14" /></svg>
          
          {t('Nouveau client')}
        </button>
      </header>

      {/* ── Les 4 repères (maquette clKpis) ───────────────────────────────────────────────────────── */}
      {resume && (
        <KpiRow items={kpis} style={{ marginBottom: 10 }} />
      )}

      <NumberingBanner gln={gln} />

      <DataTable<ClientRow>
        tableId={TABLE_ID}
        unit={{ one: t('client'), gender: 'm' }}
        columns={colonnes}
        rows={rows}
        getRowId={(c) => c.id}
        status={status}
        sort={sort}
        onSortChange={(next) => { setSort(next); setPageIndex(0); }}
        defaultSort={DEFAULT_SORT}
        page={{ index: pageIndex, size: pageSize, total }}
        onPageChange={setPageIndex}
        onPageSizeChange={(n) => { setPageSize(n); setPageIndex(0); }}
        columnLayout={layout}
        onColumnLayoutChange={setLayout}
        columnPersistenceNote={memoKo ? t('Le dernier réglage n’a PAS pu être mémorisé : {memoKo}', { memoKo }) : undefined}
        // Une fiche ANONYMISÉE se lit autrement : l'identité est détachée, la ligne est mise à l'écart.
        rowTone={(c) => (c.erased ? 'muted' : undefined)}
        onRowActivate={(c) => setFiche({ id: c.id })}
        filterChips={puces}
        filtersActive={puces.length > 0}
        onClearFilters={() => { setQ(''); setChip(''); setPageIndex(0); }}
        toolbar={(
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', flex: 1 }}>
            {/* ⚠️ CAPSULE `field-search` DU KIT — le placeholder promet CE QUE LE SQL TIENT. */}
            <div className="field-search">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden>
                <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.5-4.5" />
              </svg>
              <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={t('Nom, carte, téléphone, email…')} aria-label={t('Rechercher un client')} />
            </div>
            <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
              {CHIPS.map((c) => {
                const on = chip === c.v;
                const n = resume?.chips[c.v];
                return (
                  <button key={c.v} type="button" onClick={() => { setChip(on ? '' : c.v); setPageIndex(0); }}
                    style={{
                      height: 30, padding: '0 11px', border: `1px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`,
                      borderRadius: 'var(--r-pill)', background: on ? 'var(--teal-100)' : 'var(--paper)',
                      color: on ? 'var(--teal-800)' : 'var(--ink-600)', fontSize: '12px', fontWeight: 600,
                      display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', cursor: 'pointer',
                    }}>{t(c.l)}{n != null && <span style={{ fontVariantNumeric: 'tabular-nums', opacity: .75 }}>{n.toLocaleString('fr-FR')}</span>}</button>
                );
              })}
            </span>
          </div>
        )}
        emptyStates={{
          empty: {
            icon: 'users',
            title: t('Aucun client dans ce magasin'),
            note: t('Le fichier se remplit par la caisse (création à l’encaissement), par la reprise et depuis cet écran. Il n’y a encore personne.'),
          },
          noMatch: {
            title: t('Aucun client ne correspond'),
            note: t('Essayez un autre nom, une carte, ou retirez un filtre.'),
          },
        }}
      />

      {record && (
        <FicheClient id={record.id} onDone={(reload) => {
          setFiche(null);
          // Une écriture a eu lieu : la liste et ses repères relisent la réalité (jamais un patch local).
          if (reload) void load();
        }} />
      )}
    </>
  );
}
